/**
 * Methods to help with handling dates in JavaScript.
 */
import { differenceInDays, format, parse } from "date-fns";
import { de, enUS } from "date-fns/locale";

import i18n, { SWISS_GERMAN_LOCALE } from "../translations/i18n";

/**
 * Date formats used by different locales.
 */
export const getDateFnsLocale = () => {
    const lang = i18n.language;
    return lang === SWISS_GERMAN_LOCALE ? de : enUS;
};

export enum DateFormat {
    DATE = "dd.MM.yyyy",
    DATE_TIME = "dd.MM.yyyy HH:mm",
    TIME = "HH:mm",
}

/**
 * Formats timestamp using selected language (locale of that language).
 * Returns String formatted date.
 */
export function formatDate(date: string | undefined | Date | number, dateFormat: DateFormat): string {
    if (date) {
        return format(new Date(date), dateFormat, {
            locale: getDateFnsLocale(),
        });
    }
    return "undefined";
}

/**
 * Returns difference between 2 dates in days (int).
 * startDate included, endDate not included.
 * Time excluded from calculation, only days taken into account.
 */
export function getDifferenceInDays(startDate: string, endDate: string) {
    const parsedStartDate = parse(startDate, "dd.MM.yyyy", new Date());
    const parsedEndDate = parse(endDate, "dd.MM.yyyy", new Date());
    return -1 * differenceInDays(new Date(parsedStartDate), new Date(parsedEndDate));
}

export function addYears(timestamp: string, numberOfYearsToAdd: number) {
    const date = timestampToDate(timestamp);

    const year = date.getFullYear();
    const month = date.getMonth();
    const day = date.getDate();

    const futureDate = new Date(year + numberOfYearsToAdd, month, day);

    return formatDate(futureDate, DateFormat.DATE);
}

/**
 * Converts a timestamp in miliseconds to a date object.
 */
export function timestampToDate(timestamp: string) {
    if (timestamp === undefined) {
        throw new Error("timestamp object must be given.");
    }
    let date = new Date(+timestamp);
    return date;
}

/**
 * Returns a year (int) for a given timestamp.
 */
export function getYear(timestamp: string) {
    return timestampToDate(timestamp).getFullYear();
}

/**
 * Returns the index (zero-based) of the month for a given timestamp.
 * E.G. 3 is April.
 */
export function getMonth(timestamp: string) {
    return timestampToDate(timestamp).getMonth();
}

/**
 * Returns the day of the month for a given timestamp.
 */
export function getDay(timestamp: string) {
    return timestampToDate(timestamp).getDate();
}
